/**
 * Badge and StatusBadge.
 *
 *   <StatusBadge status={log.status} />                 any raw API status, mapped through lib/status.ts
 *   <StatusBadge status="running" />                    "Running", blue, pulsing dot
 *   <Badge variant="success" label="Active" />
 *   <Badge variant="info" label="New" showDot={false} />
 *
 * variant  success | error | warning | info | default | neutral
 * Text is 12px for every size except font="sm"/"base", which exist for older call sites. Labels render as given
 * (sentence case); the old `capitalize` styling is gone so "In progress" never shows as "In Progress".
 */
import { getStatus } from "@/lib/status";

export type BadgeVariant = "success" | "error" | "warning" | "info" | "default" | "neutral";

interface BadgeProps {
  variant: BadgeVariant;
  label: string;
  showDot?: boolean;
  /** Animate the dot (used for running jobs). */
  pulse?: boolean;
  className?: string;
  font?: "xs" | "sm" | "base";
}

const VARIANT_STYLES: Record<BadgeVariant, { bg: string; text: string; dot: string }> = {
  success: {
    bg: "bg-success-subtle",
    text: "text-success-text",
    dot: "bg-success",
  },
  error: {
    bg: "bg-danger-subtle",
    text: "text-danger-text",
    dot: "bg-danger",
  },
  warning: {
    bg: "bg-warning-subtle",
    text: "text-warning-text",
    dot: "bg-warning",
  },
  info: {
    bg: "bg-primary-subtle",
    text: "text-primary-text",
    dot: "bg-primary",
  },
  default: {
    bg: "bg-gray-100 dark:bg-white/10",
    text: "text-gray-700 dark:text-gray-300",
    dot: "bg-gray-500 dark:bg-gray-400",
  },
  neutral: {
    bg: "bg-gray-100 dark:bg-white/10",
    text: "text-fg-muted",
    dot: "bg-gray-400 dark:bg-gray-500",
  },
};

// Older call sites passed lower-case API values ("success", "failed") and relied on CSS capitalize.
const sentence = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export const Badge = ({ variant, label, showDot = true, pulse = false, className = "", font = "xs" }: BadgeProps) => {
  const styles = VARIANT_STYLES[variant] ?? VARIANT_STYLES.default;
  const textSize = font === "base" ? "text-sm" : "text-xs";

  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 font-medium leading-4 ${textSize} ${styles.bg} ${styles.text} ${className}`}
    >
      {showDot && (
        <span className="relative flex h-1.5 w-1.5 shrink-0" aria-hidden="true">
          {pulse && <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${styles.dot}`} />}
          <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${styles.dot}`} />
        </span>
      )}
      {sentence(label)}
    </span>
  );
};

export function StatusBadge({ status, className = "" }: { status: string | null | undefined; className?: string }) {
  const s = getStatus(status);
  return <Badge variant={s.variant} label={s.label} pulse={s.key === "running"} className={className} />;
}

export default Badge;
